import { useEffect } from 'react';

// Fixed elements use the layout viewport, while the keyboard reduces the
// visual viewport on iOS. Include offsetTop when Safari pans to a focused input.
export function observeKeyboardInset() {
  const viewport = window.visualViewport;
  if (!viewport) return undefined;

  const root = document.documentElement;
  let frame;

  // Mobile URL-bar show/hide also resizes the visual viewport during plain
  // scrolling, which made fixed footers drift. A keyboard can only be open
  // while an editable element has focus — gate the inset on that.
  const isKeyboardTarget = (el) =>
    !!el &&
    (el.tagName === 'INPUT' ||
      el.tagName === 'TEXTAREA' ||
      el.tagName === 'SELECT' ||
      el.isContentEditable);

  const update = () => {
    frame = undefined;
    // Pinch zoom also shrinks the visual viewport; do not treat it as a keyboard.
    const inset =
      isKeyboardTarget(document.activeElement) && Math.abs(viewport.scale - 1) < 0.01
        ? Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop)
        : 0;
    root.style.setProperty('--KeyboardInset', `${inset}px`);
  };
  const scheduleUpdate = () => {
    if (frame === undefined) frame = window.requestAnimationFrame(update);
  };

  update();
  viewport.addEventListener('resize', scheduleUpdate);
  viewport.addEventListener('scroll', scheduleUpdate);
  window.addEventListener('resize', scheduleUpdate);
  window.addEventListener('focusin', scheduleUpdate);
  window.addEventListener('focusout', scheduleUpdate);

  return () => {
    viewport.removeEventListener('resize', scheduleUpdate);
    viewport.removeEventListener('scroll', scheduleUpdate);
    window.removeEventListener('resize', scheduleUpdate);
    window.removeEventListener('focusin', scheduleUpdate);
    window.removeEventListener('focusout', scheduleUpdate);
    if (frame !== undefined) window.cancelAnimationFrame(frame);
    root.style.removeProperty('--KeyboardInset');
  };
}

export function useKeyboardInset() {
  useEffect(observeKeyboardInset, []);
}
